import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Key,
  Coins,
  ArrowRightLeft,
  Globe,
  Webhook,
  Fuel,
  Activity,
  Settings,
  LogOut,
  Clock,
  Vault,
  Landmark,
  ShieldCheck,
  Fingerprint,
  Link,
  CreditCard,
  BarChart3,
  ChevronDown,
  Shield,
  Building2,
  Users,
  Receipt,
  UserCheck,
  Tag,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuthStore } from '../stores/auth';
import { useBillingStore } from '../stores/billing';
import * as api from '../lib/api';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
  defaultOpen?: boolean;
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: '',
    defaultOpen: true,
    items: [
      { to: '/', label: 'Overview', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Core',
    defaultOpen: true,
    items: [
      { to: '/api-keys', label: 'API Keys', icon: Key },
      { to: '/connect', label: 'Connect', icon: Fingerprint },
      { to: '/tokens', label: 'Tokens', icon: Coins },
      { to: '/transactions', label: 'Transactions', icon: ArrowRightLeft },
    ],
  },
  {
    label: 'Infrastructure',
    defaultOpen: true,
    items: [
      { to: '/networks', label: 'Networks', icon: Globe },
      { to: '/webhooks', label: 'Webhooks', icon: Webhook },
      { to: '/gas', label: 'Gas & Funding', icon: Fuel },
      { to: '/events', label: 'Activity', icon: Activity },
    ],
  },
  {
    label: 'Compliance',
    defaultOpen: false,
    items: [
      { to: '/identities', label: 'Identities', icon: UserCheck },
      { to: '/compliance', label: 'Modules', icon: Shield },
      { to: '/trusted-issuers', label: 'Trusted Issuers', icon: ShieldCheck },
      { to: '/claim-topics', label: 'Claim Topics', icon: Tag },
    ],
  },
  {
    label: 'Advanced',
    defaultOpen: false,
    items: [
      { to: '/vesting', label: 'Vesting', icon: Clock },
      { to: '/vaults', label: 'Vaults', icon: Vault },
      { to: '/governance', label: 'Governance', icon: Landmark },
      { to: '/multisig', label: 'Multisig', icon: ShieldCheck },
      { to: '/tba', label: 'Token Accounts', icon: Fingerprint },
      { to: '/price-feeds', label: 'Price Feeds', icon: Link },
      { to: '/vrf', label: 'VRF', icon: Link },
    ],
  },
  {
    label: '',
    defaultOpen: true,
    items: [
      { to: '/billing', label: 'Billing', icon: CreditCard, badge: true },
      { to: '/analytics', label: 'Analytics', icon: BarChart3 },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

export function Sidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const logout = useAuthStore((s) => s.logout);
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const pendingCount = useBillingStore((s) => s.pendingCount);
  const [healthOk, setHealthOk] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const h = await api.getHealth();
        if (!cancelled) setHealthOk(h.status === 'ok');
      } catch {
        if (!cancelled) setHealthOk(false);
      }
    })();
    const interval = setInterval(() => {
      void (async () => {
        try {
          const h = await api.getHealth();
          if (!cancelled) setHealthOk(h.status === 'ok');
        } catch {
          if (!cancelled) setHealthOk(false);
        }
      })();
    }, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-zinc-800 bg-surface-1 transform transition-transform duration-200 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
      {/* ── Brand ──────────────────────────────────────── */}
      <div className="flex h-16 items-center gap-3 border-b border-zinc-800 px-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">
          U
        </div>
        <span className="text-lg font-semibold tracking-tight text-white">urblock</span>
        {healthOk !== null && (
          <span
            className={cn(
              'ml-auto inline-block h-2.5 w-2.5 rounded-full',
              healthOk ? 'bg-emerald-400' : 'bg-red-400',
            )}
            title={healthOk ? 'API healthy' : 'API unreachable'}
            aria-label={healthOk ? 'API healthy' : 'API unreachable'}
          />
        )}
      </div>

      {/* ── Tenant ─────────────────────────────────────── */}
      {tenant && (
        <div className="border-b border-zinc-800 px-6 py-3">
          <p className="truncate text-sm font-medium text-zinc-300">{tenant.company_name}</p>
          <p className="text-xs text-zinc-500">{tenant.id}</p>
          <div className="mt-2">
            <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-brand-950 text-brand-400">
              Connect mode
            </span>
          </div>
        </div>
      )}

      {/* ── Navigation ─────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {NAV_SECTIONS.map((section, sIdx) => (
          <SidebarSection key={sIdx} section={section} pendingCount={pendingCount} onNavigate={onClose} />
        ))}
        {/* Admin Panel — only for admin role */}
        {user?.role === 'admin' && (
          <>
            <div className="mt-4">
              <span className="px-3 text-[10px] font-semibold uppercase tracking-widest text-red-500">Admin</span>
            </div>
            <div className="mt-1 space-y-0.5">
              {[
                { to: '/admin', label: 'Admin Panel', icon: Shield },
                { to: '/admin/tenants', label: 'Tenants', icon: Building2 },
                { to: '/admin/users', label: 'Users', icon: Users },
                { to: '/admin/payments', label: 'Payments', icon: Receipt },
                { to: '/admin/billing', label: 'Manual Billing', icon: ShieldCheck },
                { to: '/admin/leads', label: 'Studio Leads', icon: UserCheck },
              ].map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/admin'}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-red-600/10 text-red-400'
                        : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </div>
          </>
        )}
      </nav>

      {/* ── Logout ─────────────────────────────────────── */}
      <div className="border-t border-zinc-800 p-3">
        <button type="button"
          onClick={() => void logout()}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-red-400"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Logout
        </button>
      </div>
      </aside>
    </>
  );
}

function SidebarSection({ section, pendingCount, onNavigate }: { section: NavSection; pendingCount: number; onNavigate?: () => void }) {
  const [open, setOpen] = useState(section.defaultOpen ?? true);
  const hasLabel = section.label !== '';

  return (
    <div className={hasLabel ? 'mt-4' : ''}>
      {hasLabel && (
        <button type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex w-full items-center justify-between px-3 pb-1 pt-2"
        >
          <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{section.label}</span>
          <ChevronDown className={cn('h-3 w-3 text-zinc-600 transition-transform', !open && '-rotate-90')} />
        </button>
      )}
      {open && (
        <div className="space-y-0.5">
          {section.items.map(({ to, label, icon: Icon, badge }) => {
            const hasBadge = badge && pendingCount > 0;
            return (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-brand-600/10 text-brand-400'
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
                {hasBadge && (
                  <span className="ml-auto inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
